import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jobSeekerRequestRepository } from '../../../landing/infrastructure/jobSeekerRequestApiRepository';
import { jobCategoryRepository } from '../../../landing/infrastructure/jobCategoryApiRepository';

/* ── 상수 ── */
const APPLICATION_STATUS_LABEL = {
    APPLIED: '신청함',
    CANCELLED: '취소함',
};

const EXP_LABEL     = { NEW: '신입', EXPERIENCED: '경력', ANY: '신입/경력' };
const GENDER_LABEL  = { M: '남성', F: '여성' };
const FILE_TYPE_LABEL = { RESUME: '이력서', ETC: '기타 첨부' };
const APPLICANT_TYPE_LABEL = { AGENCY_CEO: '보험 대리점 대표', HR_MANAGER: '채용 담당자' };

/* ── 유틸 ── */
function formatPhone(phone) {
    if (!phone) return null;
    const p = phone.replace(/\D/g, '');
    if (p.length === 11) return `${p.slice(0,3)}-${p.slice(3,7)}-${p.slice(7)}`;
    if (p.length === 10) return `${p.slice(0,3)}-${p.slice(3,6)}-${p.slice(6)}`;
    return phone;
}

function formatPeriodDate(str) {
    if (!str) return null;
    const [y, m] = String(str).split('-');
    return m ? `${y}.${m.padStart(2, '0')}` : y;
}

function calcDuration(startStr, endStr, isCurrent) {
    if (!startStr) return null;
    const [sy, sm = '01'] = startStr.split('-');
    const start = new Date(Number(sy), Number(sm) - 1, 1);
    const end = isCurrent ? new Date() : (() => {
        if (!endStr) return null;
        const [ey, em = '01'] = endStr.split('-');
        return new Date(Number(ey), Number(em) - 1, 1);
    })();
    if (!end) return null;
    const total = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (total < 0) return null;
    const y = Math.floor(total / 12), mo = total % 12;
    if (y === 0) return `${mo}개월`;
    if (mo === 0) return `${y}년`;
    return `${y}년 ${mo}개월`;
}

function calcAge(str) {
    if (!str) return null;
    const birth = new Date(str);
    if (isNaN(birth)) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    if (today.getMonth() < birth.getMonth() ||
        (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
    return age;
}

/* ── 공통 레이아웃 ── */
function Card({ children, style }) {
    return (
        <div style={{ background: '#fff', borderRadius: 16, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16, ...style }}>
            {children}
        </div>
    );
}

function SectionTitle({ children }) {
    return (
        <div style={{ fontSize: 11, fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>
            {children}
        </div>
    );
}

function InfoRow({ label, value }) {
    if (value == null || value === '' || value === false) return null;
    return (
        <div style={{ display: 'flex', gap: 12, padding: '9px 0', borderBottom: '1px solid #f5f5f5', fontSize: 14, alignItems: 'flex-start' }}>
            <span style={{ minWidth: 72, color: '#bbb', flexShrink: 0, paddingTop: 1 }}>{label}</span>
            <span style={{ color: '#1a1a1a', fontWeight: 500, lineHeight: 1.5 }}>{value === true ? '예' : value}</span>
        </div>
    );
}

/* ── 상태 배지 ── */
function StatusBadge({ status }) {
    const label = APPLICATION_STATUS_LABEL[status] || '신청함';
    const isCancelled = status === 'CANCELLED';
    const color  = isCancelled ? '#999' : '#2e7d32';
    const bg     = isCancelled ? '#f5f5f5' : '#f0faf0';
    const border = isCancelled ? '#e8e8e8' : '#c8e6c9';
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, color, background: bg, border: `1px solid ${border}` }}>
            {label}
        </span>
    );
}

/* ── 파일 다운로드 버튼 ── */
function FileDownloadButton({ file }) {
    const [loading, setLoading] = useState(false);

    async function handleDownload() {
        setLoading(true);
        try {
            const url = await jobSeekerRequestRepository.getDownloadUrl(file.fileKey);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            alert('다운로드 링크 생성에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }

    const typeLabel = FILE_TYPE_LABEL[file.fileType] || file.fileType || '파일';

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', border: '1px solid #f0f0f0', borderRadius: 10, gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>📄</span>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.originalFileName || '파일'}
                    </div>
                    <div style={{ fontSize: 11, color: '#bbb', marginTop: 1 }}>{typeLabel}</div>
                </div>
            </div>
            <button
                onClick={handleDownload}
                disabled={loading}
                style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#555', fontSize: 13, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1, whiteSpace: 'nowrap' }}
            >
                {loading ? '...' : '다운로드'}
            </button>
        </div>
    );
}

/* ── 메인 페이지 ── */
export function ReceivedApplicationDetailPage() {
    const navigate          = useNavigate();
    const location          = useLocation();

    const application = location.state?.application || null;

    const [seekerProfile, setSeekerProfile] = useState(null);
    const [catMap,        setCatMap]        = useState({});
    const [loadingProfile, setLoadingProfile] = useState(false);

    useEffect(() => {
        if (!application?.jobSeekerRequestId) return;
        setLoadingProfile(true);
        Promise.all([
            jobSeekerRequestRepository.getById(application.jobSeekerRequestId),
            jobCategoryRepository.getJobCategories(),
        ])
            .then(([profile, cats]) => {
                setSeekerProfile(profile);
                const map = {};
                cats.forEach(c => { map[c.code] = c.name; });
                setCatMap(map);
            })
            .catch(() => {})
            .finally(() => setLoadingProfile(false));
    }, [application?.jobSeekerRequestId]);

    if (!application) {
        navigate('/account?tab=received', { replace: true });
        return null;
    }

    /* 구직 프로필 파싱 */
    const careers  = (() => { try { return JSON.parse(seekerProfile?.careerHistories || '[]'); } catch { return []; } })();
    const cats     = (seekerProfile?.jobCategoryCodes || []).map(c => catMap[c] || c);
    const age      = calcAge(seekerProfile?.birthDate);
    const files    = seekerProfile?.files || [];
    const fullAddress = [seekerProfile?.address, seekerProfile?.addressDetail].filter(Boolean).join(' ');

    return (
        <div style={{ minHeight: '100vh', background: '#f8f9fa', paddingBottom: 60 }}>

            {/* 상단 헤더 */}
            <div style={{ background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 20px', height: 52, display: 'flex', alignItems: 'center' }}>
                    <button
                        onClick={() => navigate('/account?tab=received')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', fontSize: 14, display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}
                    >
                        ← 받은 신청서
                    </button>
                </div>
            </div>

            <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 20px 0' }}>

                {/* ── 지원자 헤더 ── */}
                <Card>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                {application.applicantType && (
                                    <span style={{ fontSize: 11, fontWeight: 700, background: '#f0f7ff', color: '#1565c0', padding: '2px 8px', borderRadius: 4 }}>
                                        {APPLICANT_TYPE_LABEL[application.applicantType] || application.applicantType}
                                    </span>
                                )}
                                {application.applicantWithdrawn && (
                                    <span style={{ fontSize: 11, fontWeight: 700, background: '#f5f5f5', color: '#999', padding: '2px 8px', borderRadius: 4, border: '1px solid #e0e0e0' }}>탈퇴 회원</span>
                                )}
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginBottom: 6 }}>
                                {application.applicantName || '이름 미제공'}
                            </div>
                            <div style={{ fontSize: 13, color: '#aaa', lineHeight: 1.6 }}>
                                {[
                                    EXP_LABEL[application.jobExperienceLevel] || application.jobExperienceLevel,
                                    application.applicantAddress?.split(' ').slice(0, 2).join(' ')
                                ].filter(Boolean).join(' · ')}
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
                            <StatusBadge status={application.status} />
                        </div>
                    </div>
                </Card>

                {/* ── 신청 정보 ── */}
                <Card>
                    <SectionTitle>신청 정보</SectionTitle>
                    <InfoRow label="이메일"  value={application.applicantEmail} />
                    <InfoRow label="연락처"  value={formatPhone(application.applicantPhone)} />
                    <InfoRow label="신청일"  value={application.appliedAt ? String(application.appliedAt).slice(0, 10) : null} />
                    {application.message && (
                        <div style={{ marginTop: 14, padding: '12px 14px', background: '#f8f9fa', borderRadius: 10 }}>
                            <div style={{ fontSize: 11, color: '#bbb', marginBottom: 6, fontWeight: 600 }}>지원 메시지</div>
                            <div style={{ fontSize: 14, color: '#444', lineHeight: 1.7 }}>"{application.message}"</div>
                        </div>
                    )}
                </Card>

                {/* ── 구직 프로필 ── */}
                <Card>
                    <SectionTitle>구직 프로필</SectionTitle>

                    {/* jobSeekerRequestId 없을 때: application 데이터만 표시 */}
                    {!application.jobSeekerRequestId && (
                        <div>
                            <InfoRow label="경력"   value={EXP_LABEL[application.jobExperienceLevel] || application.jobExperienceLevel} />
                            <InfoRow label="지역"   value={application.applicantAddress?.split(' ').slice(0, 2).join(' ')} />
                            {!application.jobExperienceLevel && !application.applicantAddress && (
                                <div style={{ textAlign: 'center', color: '#bbb', fontSize: 14, padding: '12px 0' }}>
                                    구직 신청서를 첨부하지 않은 지원입니다.
                                </div>
                            )}
                        </div>
                    )}

                    {application.jobSeekerRequestId && loadingProfile && (
                            <div style={{ textAlign: 'center', color: '#bbb', fontSize: 14, padding: '20px 0' }}>불러오는 중...</div>
                        )}

                        {!loadingProfile && seekerProfile && (
                            <>
                                {/* 기본 정보 */}
                                <div style={{ marginBottom: 24 }}>
                                    <div style={{ fontSize: 12, color: '#ddd', fontWeight: 600, marginBottom: 4 }}>기본 정보</div>
                                    <InfoRow label="성별"   value={GENDER_LABEL[seekerProfile.gender] || seekerProfile.gender} />
                                    <InfoRow label="나이"   value={age != null ? `${age}세` : null} />
                                    <InfoRow label="경력"   value={EXP_LABEL[seekerProfile.jobExperienceLevel] || seekerProfile.jobExperienceLevel} />
                                    <InfoRow label="학력"   value={seekerProfile.educationLevel} />
                                    <InfoRow label="주소"   value={fullAddress || null} />
                                </div>

                                {/* 희망 직종 */}
                                {cats.length > 0 && (
                                    <div style={{ marginBottom: 24 }}>
                                        <div style={{ fontSize: 12, color: '#ddd', fontWeight: 600, marginBottom: 10 }}>희망 직종</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {cats.map((c, i) => (
                                                <span key={i} style={{ padding: '4px 12px', borderRadius: 20, background: '#f0f7ff', color: '#1565c0', fontSize: 13, fontWeight: 500 }}>{c}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* 경력 사항 */}
                                {careers.length > 0 && (
                                    <div style={{ marginBottom: files.length > 0 ? 24 : 0 }}>
                                        <div style={{ fontSize: 12, color: '#ddd', fontWeight: 600, marginBottom: 10 }}>경력 사항</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {careers.map((c, i) => {
                                                const startFmt  = formatPeriodDate(c.startDate);
                                                const endFmt    = c.isCurrent ? '재직 중' : formatPeriodDate(c.endDate);
                                                const duration  = calcDuration(c.startDate, c.endDate, c.isCurrent);
                                                return (
                                                    <div key={i} style={{ borderRadius: 10, border: '1px solid #f0f0f0', padding: '14px 16px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                                            <span style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>{c.companyName || '회사명 미입력'}</span>
                                                            {c.employmentType && (
                                                                <span style={{ fontSize: 11, background: '#f5f5f5', color: '#888', padding: '2px 7px', borderRadius: 4 }}>{c.employmentType}</span>
                                                            )}
                                                            {c.isCurrent && (
                                                                <span style={{ fontSize: 11, background: '#e8f5e9', color: '#2e7d32', padding: '2px 7px', borderRadius: 4, fontWeight: 600 }}>재직 중</span>
                                                            )}
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                                            {c.position && <div style={{ fontSize: 13, color: '#666' }}>직위: {c.position}</div>}
                                                            {c.jobRole  && <div style={{ fontSize: 13, color: '#666' }}>담당: {c.jobRole}</div>}
                                                            {(startFmt || endFmt) && (
                                                                <div style={{ fontSize: 13, color: '#999' }}>
                                                                    {startFmt || '?'} ~ {endFmt || '?'}
                                                                    {duration && <span style={{ marginLeft: 8, color: '#bbb' }}>· {duration}</span>}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {c.mainDuties && (
                                                            <div style={{ marginTop: 10, padding: '8px 10px', background: '#f8f9fa', borderRadius: 6, fontSize: 13, color: '#555', lineHeight: 1.7 }}>
                                                                {c.mainDuties}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* 첨부 파일 */}
                                {files.length > 0 && (
                                    <div>
                                        <div style={{ fontSize: 12, color: '#ddd', fontWeight: 600, marginBottom: 10 }}>첨부 파일</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {files.map((f, i) => <FileDownloadButton key={i} file={f} />)}
                                        </div>
                                    </div>
                                )}

                                {careers.length === 0 && cats.length === 0 && files.length === 0 && !seekerProfile.gender && (
                                    <div style={{ textAlign: 'center', color: '#bbb', fontSize: 14, padding: '12px 0' }}>구직 프로필 상세 정보가 없습니다.</div>
                                )}
                            </>
                        )}

                    {application.jobSeekerRequestId && !loadingProfile && !seekerProfile && (
                        <div style={{ textAlign: 'center', color: '#bbb', fontSize: 14, padding: '12px 0' }}>구직 프로필을 불러올 수 없습니다.</div>
                    )}
                </Card>

            </div>
        </div>
    );
}
